import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';
import QuestionImage from '../components/QuestionImage.jsx';
import ModalClose from '../components/ModalClose.jsx';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator.jsx';
import { usePullToRefresh } from '../hooks/usePullToRefresh.js';
import { markDuelSeen } from '../hooks/useDuelNotifications.js';

const AVATAR_MAP = {
  'astronaut': '🧑‍🚀', 'scientist': '👩‍🔬', 'wizard': '🧙', 'hero': '🦸',
  'cat': '🐱', 'dog': '🐶', 'fox': '🦊', 'panda': '🐼', 'lion': '🦁',
  'frog': '🐸', 'octopus': '🐙', 'unicorn': '🦄', 'dragon': '🐲', 'robot': '🤖',
};
const getAvatar = (a) => AVATAR_MAP[a] || a || '🧑‍🚀';

// Niveau du duel = bande de difficulté (moyenne des thermomètres des 2 joueurs).
const DIFF = {
  decouverte: { label: 'Découverte', icon: '🐣' },
  curieux: { label: 'Curieux', icon: '🔍' },
  explorateur: { label: 'Explorateur', icon: '🧭' },
  expert: { label: 'Expert', icon: '⭐' },
};

const TABS = ['defier', 'mes-duels', 'classement'];

// ── Timer bar (same as Quiz.jsx) ──
function TimerBar({ active, duration = 25, onExpire }) {
  const [elapsed, setElapsed] = useState(0);
  const interval = useRef(null);
  const expired = useRef(false);
  useEffect(() => {
    setElapsed(0);
    expired.current = false;
    if (!active) return;
    interval.current = setInterval(() => {
      setElapsed(prev => {
        if (prev >= duration && !expired.current) {
          expired.current = true;
          if (onExpire) setTimeout(onExpire, 0);
          return duration;
        }
        return prev >= duration ? duration : prev + 0.05;
      });
    }, 50);
    return () => clearInterval(interval.current);
  }, [active, duration, onExpire]);
  const pct = Math.max(0, 1 - elapsed / duration);
  const color = pct > 0.5 ? '#51cf66' : pct > 0.25 ? '#ff922b' : '#ff7a6b';
  return (
    <div className="timer-bar-container">
      <div className="timer-bar-fill" style={{ width:`${pct*100}%`, background:color }} />
      <span className="timer-bar-text" style={{ color }}>{Math.max(0,Math.ceil(duration-elapsed))}s</span>
    </div>
  );
}

export default function Duels() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasTimer = user?.has_timer !== false;
  const [tab, setTab] = useState('defier');
  const [duels, setDuels] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Challenge flow
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [challenging, setChallenging] = useState(false);
  const [rematching, setRematching] = useState(false);

  // Play flow
  const [playingDuel, setPlayingDuel] = useState(null);
  const [duelQuestions, setDuelQuestions] = useState([]);
  const [duelDifficulty, setDuelDifficulty] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [duelAnswers, setDuelAnswers] = useState([]);
  const [duelResult, setDuelResult] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [correctAnswerId, setCorrectAnswerId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [questionKey, setQuestionKey] = useState(0);
  const [shakeWrong, setShakeWrong] = useState(false);
  // Signalement de question (porté du quiz solo)
  const [reportSent, setReportSent] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [showReportInput, setShowReportInput] = useState(false);
  const [reportReason, setReportReason] = useState('');
  // Préférence d'affichage de la fiche selon bonne/mauvaise réponse (local, défaut ON).
  const ficheVisibleFor = (isCorrect) => isCorrect
    ? localStorage.getItem('show_card_on_correct') !== '0'
    : localStorage.getItem('show_card_on_wrong') !== '0';
  const [duelFeedback, setDuelFeedback] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  // -- Lecture audio du duel (TTS pré-généré, voix Denise) : clips /tts/ servis same-origin --
  const audioOn = user?.audio_enabled === true;
  const [glowKey, setGlowKey] = useState(null);
  const [ttsReading, setTtsReading] = useState(false);
  const ttsTokenRef = useRef(0);
  const ttsAudioRef = useRef(null);
  const ttsBase = () => { const qq = duelQuestions[currentQ]; return qq?.qid ? `/tts/${qq.qid}` : null; };
  const stopTts = () => { ttsTokenRef.current++; if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (e) {} } setGlowKey(null); setTtsReading(false); };
  const playClip = (src) => new Promise((resolve) => { const a = new Audio(src); ttsAudioRef.current = a; a.onended = () => resolve(true); a.onerror = () => resolve(false); a.play().catch(() => resolve(false)); });
  // Lit l'énoncé puis chaque réponse (comme le quiz solo). Coupe toute lecture en cours.
  const readDuelQuestion = async () => {
    stopTts();
    const base = ttsBase(); const qq = duelQuestions[currentQ]; if (!base || !qq) return;
    const token = ++ttsTokenRef.current;
    setTtsReading(true);
    const ok = await playClip(`${base}/q.mp3`);
    if (!ok) { if (token === ttsTokenRef.current) setTtsReading(false); return; }
    for (const ans of (qq.answers || [])) {
      if (token !== ttsTokenRef.current) return;
      setGlowKey(ans.audio_key);
      await playClip(`${base}/${ans.audio_key}.mp3`);
      setGlowKey(null);
    }
    if (token === ttsTokenRef.current) setTtsReading(false);
  };
  useEffect(() => {
    if (playingDuel && duelQuestions.length > 0 && !duelResult && !showFeedback && audioOn) readDuelQuestion();
    return () => stopTts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ, playingDuel, duelQuestions.length]);
  const startTimeRef = useRef(null);
  const totalTimeRef = useRef(0);
  const [score, setScore] = useState(0);

  useEffect(() => { loadData(); }, []);

  // Signale aux notifications globales qu'un duel est en cours (masque le toast).
  useEffect(() => {
    const active = !!(playingDuel && duelQuestions.length > 0 && !duelResult);
    window.__curioDuelActive = active;
    return () => { window.__curioDuelActive = false; };
  }, [playingDuel, duelQuestions, duelResult]);

  // Le duel qu'on vient de jouer et dont on voit déjà le résultat ici : on le
  // marque "vu" pour que la popup-score globale ne le ré-affiche pas.
  useEffect(() => {
    if (duelResult?.complete && playingDuel?.id && user?.id) {
      markDuelSeen(user.id, playingDuel.id);
    }
  }, [duelResult, playingDuel, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [d, o, l] = await Promise.all([
        api.getMyDuels().catch(() => []),
        api.getDuelOpponents().catch(() => []),
        api.getDuelLeaderboard().catch(() => []),
      ]);
      setDuels(d);
      setOpponents(o);
      setLeaderboard(l);
    } catch {}
    setLoading(false);
  };

  // Charge les thèmes accessibles aux deux joueurs quand on choisit un adversaire
  useEffect(() => {
    if (!selectedOpponent) {
      setThemes([]);
      setSelectedTheme(null);
      return;
    }
    setThemesLoading(true);
    setSelectedTheme(null);
    api.getDuelThemesWith(selectedOpponent.id)
      .then(t => setThemes(t || []))
      .catch(() => setThemes([]))
      .finally(() => setThemesLoading(false));
  }, [selectedOpponent]);

  const handleChallenge = async () => {
    if (!selectedOpponent || !selectedTheme || challenging) return;
    setChallenging(true);
    try {
      await api.createDuel({ opponent_id: selectedOpponent.id, theme_id: selectedTheme.id });
      setSelectedOpponent(null);
      setSelectedTheme(null);
      setTab('mes-duels');
      await loadData();
    } catch (e) { alert(e.message); }
    setChallenging(false);
  };

  // Revanche : relance un défi sur le MÊME thème contre le MÊME adversaire.
  // Utilisé depuis l'écran de résultat (duel complet) ET depuis l'historique.
  const handleRematch = async (duel) => {
    if (!duel || !duel.opponent_id || !duel.theme_id || rematching) return;
    setRematching(true);
    try {
      await api.createDuel({ opponent_id: duel.opponent_id, theme_id: duel.theme_id });
      setRematching(false);
      exitDuel();
      setTab('mes-duels');
    } catch (e) { alert(e.message); setRematching(false); }
  };

  const handleAccept = async (duelId) => {
    try { await api.acceptDuel(duelId); await loadData(); } catch (e) { alert(e.message); }
  };
  const handleDecline = async (duelId) => {
    try { await api.declineDuel(duelId); await loadData(); } catch (e) { alert(e.message); }
  };

  const startPlaying = async (duel) => {
    try {
      const data = await api.getDuelQuestions(duel.id);
      // Reprise : on repart à la 1re question NON répondue (anti-triche : les questions
      // déjà répondues sont verrouillées côté serveur, on ne les rejoue pas).
      const prog = data.my_progress || [];
      const answered = prog.length;
      const resumeScore = prog.filter(p => p.is_correct).length;
      setDuelQuestions(data.questions);
      setDuelDifficulty(data.difficulty || null);
      setPlayingDuel(duel);
      setDuelAnswers(prog.map(p => ({ question_id: null, answer_id: p.answer_id, time_spent_ms: 0 })));
      setDuelResult(null);
      setSelectedAnswer(null);
      setCorrectAnswerId(null);
      setShowFeedback(false);
      setDuelFeedback(null);
      setTimedOut(false);
      setScore(resumeScore);
      setQuestionKey(answered);
      totalTimeRef.current = 0;
      startTimeRef.current = Date.now();
      if (answered >= data.questions.length && data.questions.length > 0) {
        // Tout était répondu mais pas soumis → on finalise directement.
        setCurrentQ(data.questions.length - 1);
        const result = await api.submitDuelAnswers(duel.id, { answers: [], total_time_ms: 0 });
        setDuelResult(result);
      } else {
        setCurrentQ(answered);
      }
    } catch (e) { alert(e.message); }
  };

  const handleTimeExpire = () => {
    if (selectedAnswer || timedOut) return;
    setTimedOut(true);
    handleDuelAnswer(null);
  };

  const handleDuelAnswer = async (answerId) => {
    if (selectedAnswer || (showFeedback && !timedOut)) return;
    const timeSpent = Date.now() - startTimeRef.current;
    totalTimeRef.current += timeSpent;
    setSelectedAnswer(answerId || 'timeout');

    const q = duelQuestions[currentQ];

    // Ask backend for the correct answer + full quiz processing
    try {
      const res = await api.answerDuelQuestion(playingDuel.id, {
        question_id: q.id,
        answer_id: answerId || null,
        time_spent_ms: timeSpent,
        question_index: currentQ,
      });

      setCorrectAnswerId(res.correct_answer_id);
      setDuelFeedback(res);
      if (res.is_correct) setScore(prev => prev + 1);
      if (!res.is_correct && answerId) setShakeWrong(true);

      const answer = {
        question_id: q.id,
        answer_id: answerId || null,
        time_spent_ms: timeSpent,
      };
      setDuelAnswers(prev => [...prev, answer]);
      setShowFeedback(true);
    } catch (e) {
      // Fallback: just continue
      setShowFeedback(true);
    }
  };

  const nextDuelQuestion = async () => {
    setShakeWrong(false);
    setTimedOut(false);

    if (currentQ + 1 < duelQuestions.length) {
      setCurrentQ(currentQ + 1);
      setSelectedAnswer(null);
      setCorrectAnswerId(null);
      setShowFeedback(false);
      setDuelFeedback(null);
      setQuestionKey(prev => prev + 1);
      setShowReportInput(false);
      setReportReason('');
      setReportSent(false);
      startTimeRef.current = Date.now();
    } else {
      // All questions answered - finalize the duel
      try {
        const result = await api.submitDuelAnswers(playingDuel.id, {
          answers: [],  // Already saved individually
          total_time_ms: totalTimeRef.current,
        });
        setDuelResult(result);
      } catch (e) { alert(e.message); }
    }
  };

  const submitReport = async () => {
    const reason = reportReason.trim();
    const qid = duelQuestions[currentQ]?.id;
    if (!qid || reporting || reportSent || !reason) return;
    setReporting(true);
    try {
      await api.reportQuestion(qid, reason);
      setReportSent(true);
      setShowReportInput(false);
    } catch (e) { /* silencieux : ne pas perturber le duel */ }
    setReporting(false);
  };

  const exitDuel = () => {
    setPlayingDuel(null);
    setDuelQuestions([]);
    setDuelResult(null);
    loadData();
  };

  // Pull-to-refresh : hook appelé AVANT tout early return (règles des hooks)
  const { pullDistance, isRefreshing, threshold, bindProps } = usePullToRefresh(loadData);

  // ═══ PLAYING A DUEL (same UI as Quiz) ═══
  if (playingDuel && duelQuestions.length > 0 && !duelResult) {
    const q = duelQuestions[currentQ];
    return (
      <motion.div className="quiz-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="quiz-header">
          <button className="back-btn" onClick={exitDuel}>✕</button>
          <div className="progress-info">
            <span style={{ fontSize: '1.02rem', fontWeight: 700 }}>
              ⚔️ Duel{playingDuel?.theme && <> · {playingDuel.theme.icon} {playingDuel.theme.name}</>}{duelDifficulty && <> · <span style={{ color: 'var(--accent)' }}>{DIFF[duelDifficulty]?.icon} {DIFF[duelDifficulty]?.label}</span></>} — {currentQ + 1}/{duelQuestions.length}
            </span>
            <span className="score-display">Score : {score}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${((currentQ + (showFeedback ? 1 : 0)) / duelQuestions.length) * 100}%` }} />
          </div>
        </div>

        {hasTimer && <TimerBar active={!showFeedback} duration={25} onExpire={handleTimeExpire} />}

        <div className="container">
          <AnimatePresence mode="wait">
            <motion.div key={questionKey}
              initial={{ opacity:0,x:40 }} animate={{ opacity:1,x:0 }}
              exit={{ opacity:0,x:-40 }} transition={{ duration:0.25 }}
              className={shakeWrong ? 'shake' : ''}>
              <div className="question-card">
                <div className="question-top-row" style={{ justifyContent: 'flex-end' }}>
                  <span className="question-number">Q{currentQ + 1}</span>
                </div>
                <QuestionImage imageUrl={q.image_url} />
                <div className="question-text">{q.text}</div>
                {!audioOn && q.qid && (
                  <button type="button" className="tts-replay-btn" onClick={readDuelQuestion}
                    disabled={ttsReading} aria-label="Ecouter la question">🔊 Ecouter</button>
                )}
              </div>

              <div className="answers-grid">
                {q.answers.map((answer, i) => {
                  let cls = 'answer-btn';
                  if (showFeedback) {
                    if (answer.id === correctAnswerId) cls += ' correct';
                    else if (answer.id === selectedAnswer) cls += ' wrong';
                    else cls += ' dimmed';
                  }
                  if (!showFeedback && glowKey && glowKey === answer.audio_key) cls += ' tts-glow';
                  return (
                    <motion.button key={answer.id} className={cls}
                      onClick={() => handleDuelAnswer(answer.id)} disabled={!!selectedAnswer}
                      style={{ touchAction: 'manipulation' }}
                      whileTap={!selectedAnswer ? { scale:0.97 } : {}}>
                      <span className="answer-letter">{String.fromCharCode(65+i)}</span>
                      <span>{answer.text}</span>
                    </motion.button>
                  );
                })}
              </div>

              {showFeedback && (
                <motion.div className="feedback" initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }}>
                  <div className="feedback-header">
                    <div className="feedback-icon">
                      {selectedAnswer === 'timeout' ? '⏰' : (selectedAnswer === correctAnswerId ? '🎉' : '💫')}
                    </div>
                    <div className={`feedback-title ${selectedAnswer === correctAnswerId ? 'correct' : 'wrong'}`}>
                      {selectedAnswer === 'timeout' ? 'Temps écoulé !'
                        : selectedAnswer === correctAnswerId ? 'Bravo !' : 'Raté !'}
                    </div>
                  </div>
                  {duelFeedback?.explanation && ficheVisibleFor(selectedAnswer === correctAnswerId) && <div className="feedback-explanation">{duelFeedback.explanation}</div>}
                  {duelFeedback?.fact_card && ficheVisibleFor(selectedAnswer === correctAnswerId) && (
                    <div className="fact-card">
                      <div className="fact-card-badge">
                        {duelFeedback.is_new_card ? '✨ Nouvelle fiche !' : '📚 Déjà collectée'}
                      </div>
                      <h4>💡 {duelFeedback.fact_card.title}</h4>
                      <p>{duelFeedback.fact_card.content}</p>
                    </div>
                  )}
                  {duelFeedback?.new_connections?.length > 0 && duelFeedback.new_connections.map((conn, i) => (
                    <motion.div key={i} className="connection-teaser"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      {conn.tag} Connexion : <strong>{conn.card_a_title}</strong> ({conn.card_a_theme})
                      ↔ <strong>{conn.card_b_title}</strong> ({conn.card_b_theme})
                      <div style={{ fontSize: '0.85rem', marginTop: 4, opacity: 0.8 }}>{conn.explanation}</div>
                    </motion.div>
                  ))}
                  <button className="btn btn-primary next-btn" onClick={nextDuelQuestion}>
                    {currentQ + 1 >= duelQuestions.length ? '🏆 Voir le résultat' : '➡️ Question suivante'}
                  </button>
                  <button className="report-question-btn" onClick={() => setShowReportInput(true)} disabled={reportSent}>
                    {reportSent ? '✅ Question signalée, merci !' : '🚩 Signaler cette question'}
                  </button>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {showReportInput && (
          <div className="report-modal-overlay" onClick={() => { if (!reporting) { setShowReportInput(false); setReportReason(''); } }}>
            <div className="report-modal" onClick={(e) => e.stopPropagation()}>
              <ModalClose onClick={() => { setShowReportInput(false); setReportReason(''); }} />
              <h3 className="report-modal-title">🚩 Signaler cette question</h3>
              <p className="report-modal-sub">Dis-nous ce qui ne va pas (obligatoire) :</p>
              <textarea
                className="report-textarea"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Ex : l'image ne correspond pas, la réponse est fausse, faute d'orthographe…"
                rows={4}
                autoFocus
              />
              <div className="report-box-actions">
                <button className="report-cancel-btn" onClick={() => { setShowReportInput(false); setReportReason(''); }} disabled={reporting}>
                  Annuler
                </button>
                <button className="report-send-btn" onClick={submitReport} disabled={reporting || !reportReason.trim()}>
                  {reporting ? '⏳ Envoi…' : 'Envoyer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // ═══ DUEL RESULT ═══
  if (duelResult) {
    return (
      <motion.div className="duel-result-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="duel-result-card">
          {duelResult.complete ? (
            <>
              <h2>{duelResult.i_won ? '🏆 Victoire !' : duelResult.i_won === false ? '🎯 Pas cette fois !' : '🤝 Égalité'}</h2>
              <div className="duel-result-versus">
                <div className="duel-result-player">
                  <span className="duel-result-name">Toi</span>
                  <span className="duel-result-score">{duelResult.score}<span className="duel-result-total">/{duelResult.total}</span></span>
                </div>
                <span className="duel-result-vs">—</span>
                <div className="duel-result-player">
                  <span className="duel-result-score">{duelResult.their_score}<span className="duel-result-total">/{duelResult.total}</span></span>
                  <span className="duel-result-name">{playingDuel?.opponent?.display_name || 'Adversaire'}</span>
                </div>
              </div>
              {duelResult.i_won === false && <p className="duel-encourage">Beau combat ! Tu feras mieux la prochaine fois 💪</p>}
            </>
          ) : (
            <>
              <h2>✅ Réponses envoyées !</h2>
              <div className="duel-result-score-solo">
                <span className="big-score">{duelResult.score}/{duelResult.total}</span>
              </div>
              <p className="duel-waiting">En attente de l'adversaire...</p>
            </>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
            {duelResult.complete && playingDuel?.opponent_id && playingDuel?.theme_id && (
              <button className="btn-login" onClick={() => handleRematch(playingDuel)} disabled={rematching}>
                {rematching ? '⏳ …' : '🔁 Revanche'}
              </button>
            )}
            <button className="btn-login" onClick={exitDuel}>Retour aux duels</button>
          </div>
        </div>
      </motion.div>
    );
  }

  // ═══ MAIN TABS ═══
  if (loading) {
    return <div className="loading-screen"><div className="loading-rocket">⚔️</div><p>Chargement...</p></div>;
  }

  const pendingForMe = duels.filter(d => d.needs_accept);
  const activeDuels = duels.filter(d => d.can_play);
  const waitingDuels = duels.filter(d => ['pending', 'challenger_done', 'opponent_done'].includes(d.status) && !d.can_play && !d.needs_accept);
  const completedDuels = duels
    .filter(d => d.status === 'complete')
    .sort((a, b) => new Date(b.completed_at || b.created_at || 0) - new Date(a.completed_at || a.created_at || 0));

  // Thèmes déjà en duel ACTIF avec l'adversaire sélectionné → on les grise (un seul
  // duel par thème/adversaire à la fois). Évite de lancer pour tomber sur une erreur.
  const busyThemeIds = selectedOpponent
    ? new Set(duels
        .filter(d => d.opponent_id === selectedOpponent.id
          && ['pending', 'accepted', 'challenger_done', 'opponent_done'].includes(d.status))
        .map(d => d.theme_id))
    : new Set();

  return (
    <motion.div className="duels-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />
      <div className="duels-content" {...bindProps}>
        <PullToRefreshIndicator distance={pullDistance} threshold={threshold} loading={isRefreshing} />
        <div className="duels-tabs">
          {TABS.map(t => (
            <button key={t} className={`duels-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t === 'mes-duels' ? `⚔️ Mes duels${pendingForMe.length ? ` (${pendingForMe.length})` : ''}` :
               t === 'defier' ? '🎯 Défier' : '🏅 Classement'}
            </button>
          ))}
        </div>

        {tab === 'mes-duels' && (
          <div className="duels-list">
            {pendingForMe.length > 0 && (
              <div className="duel-section">
                <h3>📩 Défis reçus</h3>
                {pendingForMe.map(d => (
                  <div key={d.id} className="duel-card pending">
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(d.opponent.avatar)}</span>
                      <div>
                        <strong>{d.opponent.display_name}</strong> te défie en <strong>{d.theme.icon} {d.theme.name}</strong>
                        <div className="duel-elo">ELO {d.opponent.elo_rating}{d.difficulty ? ` · ${DIFF[d.difficulty]?.icon} ${DIFF[d.difficulty]?.label}` : ''}</div>
                      </div>
                    </div>
                    <div className="duel-card-actions">
                      <button className="btn-accept" onClick={() => handleAccept(d.id)}>✅ Accepter</button>
                      <button className="btn-decline" onClick={() => handleDecline(d.id)}>❌</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeDuels.length > 0 && (
              <div className="duel-section">
                <h3>🎮 À jouer</h3>
                {activeDuels.map(d => (
                  <div key={d.id} className="duel-card active">
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(d.opponent.avatar)}</span>
                      <div>
                        <strong>{d.theme.icon} {d.theme.name}</strong> vs <strong>{d.opponent.display_name}</strong>
                        {d.difficulty && <div className="duel-status">{DIFF[d.difficulty]?.icon} Niveau {DIFF[d.difficulty]?.label}</div>}
                      </div>
                    </div>
                    <button className="btn-play" onClick={() => startPlaying(d)}>▶️ Jouer</button>
                  </div>
                ))}
              </div>
            )}

            {waitingDuels.length > 0 && (
              <div className="duel-section">
                <h3>⏳ En attente</h3>
                {waitingDuels.map(d => (
                  <div key={d.id} className="duel-card waiting">
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(d.opponent.avatar)}</span>
                      <div>
                        <strong>{d.theme.icon} {d.theme.name}</strong> vs <strong>{d.opponent.display_name}</strong>
                        <div className="duel-status">{d.status === 'pending' ? 'Défi envoyé' : 'En attente'}</div>
                      </div>
                    </div>
                    {d.my_score !== null && <span className="duel-my-score">{d.my_score}/10</span>}
                  </div>
                ))}
              </div>
            )}

            {completedDuels.length > 0 && (
              <div className="duel-section">
                <h3>📋 Terminés</h3>
                {completedDuels.slice(0, 20).map(d => (
                  <div key={d.id} className={`duel-card complete ${d.i_won ? 'won' : d.i_won === false ? 'lost' : 'draw'}`}>
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(d.opponent.avatar)}</span>
                      <div>
                        <strong>{d.theme.icon} {d.theme.name}</strong> vs <strong>{d.opponent.display_name}</strong>
                        <div className="duel-scores">{d.my_score} — {d.their_score}</div>
                      </div>
                    </div>
                    <span className={`duel-badge ${d.i_won ? 'win' : d.i_won === false ? 'loss' : 'draw'}`}>
                      {d.i_won ? '🏆' : d.i_won === false ? '🥈' : '🤝'}
                    </span>
                    <button onClick={() => handleRematch(d)} disabled={rematching} title="Revanche" aria-label="Revanche"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.25rem', marginLeft: 8, opacity: rematching ? 0.4 : 1 }}>🔁</button>
                  </div>
                ))}
              </div>
            )}

            {duels.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">⚔️</div>
                <p>Aucun duel en cours</p>
                <button className="btn-login" onClick={() => setTab('defier')}>Lancer un défi !</button>
              </div>
            )}
          </div>
        )}

        {tab === 'defier' && (
          <div className="challenge-flow">
            <h3>1. Choisis ton adversaire</h3>
            <div className="opponents-list">
              {opponents.map(o => (
                <div key={o.id}
                  className={`opponent-card ${selectedOpponent?.id === o.id ? 'selected' : ''}`}
                  onClick={() => setSelectedOpponent(o)}>
                  <span className="opp-avatar">{getAvatar(o.avatar)}</span>
                  <div>
                    <strong>{o.display_name}</strong>
                    <div className="opp-elo">ELO {o.elo_rating}</div>
                  </div>
                </div>
              ))}
              {opponents.length === 0 && <p className="no-opponents">Aucun adversaire pour l'instant. D'autres joueurs doivent créer un compte.</p>}
            </div>

            {selectedOpponent && (
              <>
                <h3>2. Choisis le thème</h3>
                {themesLoading ? (
                  <div className="theme-picker-loading">⏳ Recherche des thèmes communs...</div>
                ) : themes.length === 0 ? (
                  <div className="theme-picker-empty">
                    😕 Aucun thème commun avec {selectedOpponent.display_name} pour l'instant.
                    Débloquez les mêmes thèmes pour pouvoir vous défier !
                  </div>
                ) : (
                  <div className="theme-picker">
                    {themes.map(t => {
                      const busy = busyThemeIds.has(t.id);
                      return (
                      <div key={t.id}
                        className={`theme-pick ${selectedTheme?.id === t.id ? 'selected' : ''}`}
                        onClick={() => busy ? null : setSelectedTheme(t)}
                        aria-disabled={busy}
                        style={busy ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}>
                        {t.icon} {t.name}{busy ? ' · en cours' : ''}
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {selectedOpponent && selectedTheme && (
              <button className="btn-login challenge-btn" onClick={handleChallenge} disabled={challenging}>
                {challenging ? '⏳' : '⚔️'} Défier {selectedOpponent.display_name} en {selectedTheme.name} !
              </button>
            )}
          </div>
        )}

        {tab === 'classement' && (
          <div className="elo-leaderboard">
            <h3>🏅 Classement ELO</h3>
            {leaderboard.map((p, i) => (
              <div key={p.id} className={`lb-row ${p.is_me ? 'is-me' : ''}`}>
                <span className="lb-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}</span>
                <span className="lb-avatar">{getAvatar(p.avatar)}</span>
                <div className="lb-info">
                  <strong>{p.display_name}</strong>
                  <span className="lb-record">{p.wins}V - {p.draws || 0}N - {p.losses}D</span>
                </div>
                <span className="lb-elo">{p.elo_rating}</span>
              </div>
            ))}
            {leaderboard.length === 0 && <p className="empty-state">Aucun duel joué</p>}
          </div>
        )}
      </div>
    </motion.div>
  );
}
